import type { Session } from "@supabase/supabase-js";
import { useCallback, useEffect, useState } from "react";
import { adminRequest, type RunRow } from "../adminApi";
import { LoadingPanel } from "../components/LoadingPanel";
import { Pagination } from "../components/Pagination";
import { Status } from "../components/Status";

const PAGE_SIZE = 50;

const STATUS_OPTIONS = ["all", "queued", "running", "success", "failed", "cancelled", "waiting"];
const TRIGGER_OPTIONS = ["all", "manual", "scheduled", "widget"];

export function RunsPage({
  mfaToken,
  session,
  setNotice
}: {
  mfaToken: string | null;
  session: Session;
  setNotice(n: { tone: "error" | "success"; text: string } | null): void;
}) {
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [triggerFilter, setTriggerFilter] = useState("all");

  const loadPage = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("offset", String((p - 1) * PAGE_SIZE));
      params.set("limit", String(PAGE_SIZE));
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (triggerFilter !== "all") params.set("trigger_type", triggerFilter);

      const data = await adminRequest<{ runs: RunRow[]; total_count: number }>(
        `/runs?${params}`,
        { mfaToken, session }
      );
      setRuns(data.runs);
      setTotalCount(data.total_count);
      setPage(p);
    } catch (caught) {
      setNotice({ tone: "error", text: caught instanceof Error ? caught.message : "Could not load runs." });
    } finally {
      setLoading(false);
    }
  }, [mfaToken, session, setNotice, statusFilter, triggerFilter]);

  useEffect(() => { void loadPage(1); }, [loadPage]);

  return (
    <div className="stack">
      <section className="section">
        <div className="sectionHead">
          <div>
            <h1>Runs</h1>
            <p>All workflow runs across every user, filterable by status and trigger type.</p>
          </div>
          <button onClick={() => void loadPage(page)} type="button">Refresh</button>
        </div>

        <div className="filterBar">
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt === "all" ? "All statuses" : opt}</option>
            ))}
          </select>
          <select value={triggerFilter} onChange={(e) => { setTriggerFilter(e.target.value); setPage(1); }}>
            {TRIGGER_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt === "all" ? "All triggers" : opt}</option>
            ))}
          </select>
        </div>

        {loading ? <LoadingPanel label="Loading runs…" /> : <RunsTable runs={runs} />}
        <Pagination currentPage={page} pageSize={PAGE_SIZE} totalCount={totalCount} onPageChange={loadPage} />
      </section>
    </div>
  );
}

function RunsTable({ runs }: { runs: RunRow[] }) {
  return (
    <div className="tableWrap">
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Workflow</th>
            <th>User</th>
            <th>Vehicle</th>
            <th>Trigger</th>
            <th>Status</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => (
            <tr key={run.id}>
              <td><code>{run.id.slice(0, 8)}</code></td>
              <td>{run.workflow_name ?? "—"}</td>
              <td>{run.user_email ?? "—"}</td>
              <td>{run.vehicle_name ?? "—"}</td>
              <td><span className={`tag tag--${run.trigger_type}`}>{run.trigger_type}</span></td>
              <td><Status value={run.status} /></td>
              <td>{formatDate(run.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!runs.length ? <div className="empty">No runs match the current filters.</div> : null}
    </div>
  );
}

function formatDate(value: string) {
  return new Date(value).toLocaleString();
}
